import type { HoldemActionType, HoldemOptions } from '@tv-casino/shared';

/**
 * The floating commit, as data. The web pins a pill row over the screen when
 * the real control has scrolled away (`FloatAction`) — craps' roll was the
 * first, and every table got one. What floats is never a free choice: it
 * carries the same guards the real button carries, and never an expensive
 * move, because a pill is no place to size a bet.
 *
 * The guards that take more than one boolean to state live here, so both
 * renderers press the same rules: which hold'em moves may float, and which of
 * keno's two commits is the one the screen owes.
 */

/** How a floating pill reads: the way forward, or the way out. */
export type FloatKind = 'go' | 'quiet';

export interface FloatButtonVM {
  /** Stable id — the testID the shells hang off, and the action's name. */
  id: string;
  label: string;
  kind: FloatKind;
}

/** A watched control's edges, and the fold it is measured against. */
export interface FloatRect {
  top: number;
  bottom: number;
}

/**
 * Whether a watched control has left the page — no overlap at all, which is
 * `IntersectionObserver`'s default threshold, so the web's observer and a
 * native scroll measurement agree on the word "offscreen".
 */
export const isOffscreen = (rect: FloatRect, viewport: FloatRect): boolean =>
  rect.bottom <= viewport.top || rect.top >= viewport.bottom;

/**
 * Where a scroll view should sit so a stage measured in window coordinates
 * lands in the middle of the fold. A spin or a throw the player has scrolled
 * past is the reason: the roller has to come back into view, and a stage
 * already centred stays put (the delta is ~0). Never a negative offset.
 */
export const stageScrollY = (input: {
  scrollY: number;
  nodeTop: number;
  nodeHeight: number;
  viewportTop: number;
  viewportHeight: number;
}): number => {
  const delta = input.nodeTop - input.viewportTop;
  const centered = input.scrollY + delta - (input.viewportHeight - input.nodeHeight) / 2;
  return Math.max(0, centered);
};

export interface HoldemFloatVM extends FloatButtonVM {
  action: HoldemActionType;
}

/**
 * What floats when the decision bar itself is off-screen: the cheap way
 * forward and the way out. A free check is the whole row — there is nothing to
 * give up. Otherwise fold rides quiet beside the call, or beside the shove when
 * calling would be a shove and no raise is legal. Never a bet or a raise.
 */
export const holdemFloatButtons = (options: HoldemOptions, chips: number): HoldemFloatVM[] => {
  if (options.check) return [{ id: 'check', action: 'check', label: 'Check', kind: 'go' }];
  const out: HoldemFloatVM[] = [{ id: 'fold', action: 'fold', label: 'Fold', kind: 'quiet' }];
  if (options.call) {
    // Calling the last of a stack is a shove; say so, so nobody taps it blind.
    const allIn = chips <= options.callAmount;
    out.push({
      id: 'call',
      action: 'call',
      label: `Call ${options.callAmount.toLocaleString()}${allIn ? ' — all in' : ''}`,
      kind: 'go',
    });
  } else if (!(options.bet || options.raise) && chips > options.callAmount) {
    // Nothing to call and nothing legal to raise: the shove is the only move.
    out.push({
      id: 'all_in',
      action: 'all_in',
      label: `All in ${options.allInTo.toLocaleString()}`,
      kind: 'go',
    });
  }
  return out;
};

export interface KenoCommitState {
  /** The window takes chips and this seat has not locked in. */
  writing: boolean;
  /** A fresh ticket is legal: spots picked, stake affordable, slate not full. */
  addable: boolean;
  /** A ticket under edit is legal to replace. */
  updatable: boolean;
  /** The pad's tap replaces a ticket rather than adding one. */
  editing: boolean;
  /** The stake armed on the rail. */
  stake: number;
  /** Everything this seat already has on the board. */
  staked: number;
}

/**
 * Keno's board is the tallest panel in the house, so its commit is below the
 * fold whenever a ticket is being written. Two commits exist and only ever one
 * is owed: the pad's own tap wins while a draft stands, and Done is the tap
 * the screen owes once the slate is down with nothing staged.
 */
export interface KenoCommitFloat {
  commit: FloatButtonVM | null;
  done: FloatButtonVM | null;
}

export const kenoCommitFloat = (state: KenoCommitState): KenoCommitFloat => {
  if (state.addable || state.updatable) {
    const update = state.editing;
    return {
      commit: {
        id: update ? 'update' : 'add',
        label: `${update ? 'Update ticket' : 'Add ticket'} (${state.stake.toLocaleString()})`,
        kind: 'go',
      },
      done: null,
    };
  }
  if (state.writing && state.staked > 0) {
    return { commit: null, done: { id: 'done', label: 'Done — lock in', kind: 'go' } };
  }
  return { commit: null, done: null };
};
